import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
  aiHubListDataGridRowBorderSx,
} from "../../components/shared/regulationsListDataGridSx";
import { DataGridManageColumnsPanel } from "../../components/shared/DataGridManageColumnsPanel";
import ThinCheckbox from "../../components/shared/ThinCheckbox";
import styles from "../../components/RegulationsTable/RegulationsTable.module.css";
import AiHubNoRowsOverlay from "./AiHubNoRowsOverlay";
import type { TaggingResultRow } from "./constants";
import type { TaggingResultDisplayConcept } from "./taggingResultData";
import { AiHubFilterLabelCell, AiHubObjectIdCell } from "./aiHubTableCells";
import TaggingResultRowActions from "./TaggingResultRowActions";
import TaggingResultTableToolbar from "./TaggingResultTableToolbar";
import { useCrossPageRowSelection } from "./useCrossPageRowSelection";

type TaggingResultTableProps = Readonly<{
  rows: TaggingResultRow[];
  displayConcepts: readonly TaggingResultDisplayConcept[];
  scopeObjectType: string;
  scopeObjectSubType: string;
  objectTypeLabel: string;
  objectSubTypeLabel: string;
  rowCount: number;
  selectionResetKey: string;
  loading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  objectIdSearch: string;
  onObjectIdSearchChange: (value: string) => void;
}>;

function conceptValueCell(text: string) {
  const isEmpty = !text.trim();
  const display = isEmpty ? "-" : text;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minWidth: 0,
      }}
    >
      <Tooltip title={isEmpty ? "" : text} disableHoverListener={isEmpty}>
        <Typography
          noWrap
          sx={{
            color: isEmpty ? "#8c8c8c" : "inherit",
            fontSize: "14px",
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {display}
        </Typography>
      </Tooltip>
    </Box>
  );
}

export default function TaggingResultTable({
  rows,
  displayConcepts,
  scopeObjectType,
  scopeObjectSubType,
  objectTypeLabel,
  objectSubTypeLabel,
  rowCount,
  selectionResetKey,
  loading = false,
  paginationModel,
  onPaginationModelChange,
  objectIdSearch,
  onObjectIdSearchChange,
}: TaggingResultTableProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const manageColumnsPanelScrollTopRef = useRef(0);
  const {
    selectionModel,
    onRowSelectionModelChange,
    isRowSelectable,
    selectedRows,
  } = useCrossPageRowSelection(rows, selectionResetKey);

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={manageColumnsPanelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const toolbarSlot = useCallback(
    () => (
      <TaggingResultTableToolbar
        selectedRows={selectedRows}
        displayConcepts={displayConcepts}
        objectTypeLabel={objectTypeLabel}
        objectSubTypeLabel={objectSubTypeLabel}
        objectIdSearch={objectIdSearch}
        onObjectIdSearchChange={onObjectIdSearchChange}
      />
    ),
    [
      displayConcepts,
      objectIdSearch,
      objectSubTypeLabel,
      objectTypeLabel,
      onObjectIdSearchChange,
      selectedRows,
    ],
  );

  const hasScopeSelection = Boolean(
    scopeObjectType.trim() || scopeObjectSubType.trim(),
  );
  const noRowsOverlaySlot = useCallback(
    () => <AiHubNoRowsOverlay hasScopeSelection={hasScopeSelection} />,
    [hasScopeSelection],
  );

  const columns = useMemo<GridColDef<TaggingResultRow>[]>(
    () => [
      {
        field: "objectTypeName",
        headerName: "Object Type",
        width: 140,
        sortable: false,
        filterable: false,
        valueGetter: () => objectTypeLabel.trim() || "-",
        renderCell: () => <AiHubFilterLabelCell label={objectTypeLabel} />,
      },
      {
        field: "objectSubTypeName",
        headerName: "Object Sub-Type",
        width: 160,
        sortable: false,
        filterable: false,
        valueGetter: () => objectSubTypeLabel.trim() || "-",
        renderCell: () => <AiHubFilterLabelCell label={objectSubTypeLabel} />,
      },
      {
        field: "objectId",
        headerName: "Object ID",
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <AiHubObjectIdCell id={row.objectId} link={row.link} />
        ),
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.2,
        minWidth: 220,
        sortable: false,
        filterable: false,
      },
      {
        field: "version",
        headerName: "Version",
        width: 100,
        sortable: false,
        filterable: false,
      },
      ...displayConcepts.map((concept): GridColDef<TaggingResultRow> => ({
        field: `concept:${concept.name}`,
        headerName: concept.displayName,
        flex: 1,
        minWidth: 160,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) =>
          row.tagsByConcept[concept.name]?.trim() || "",
        renderCell: (params) =>
          conceptValueCell(
            typeof params.value === "string" ? params.value : "",
          ),
      })),
      {
        field: "actions",
        headerName: "Actions",
        width: 88,
        sortable: false,
        filterable: false,
        resizable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-hub-actions-cell",
        renderCell: ({ row }) => (
          <TaggingResultRowActions
            rowId={row.objectId}
            objectType={row.objectType || scopeObjectType}
            objectSubType={row.objectSubType || scopeObjectSubType}
            version={row.version}
          />
        ),
      },
    ],
    [
      displayConcepts,
      objectSubTypeLabel,
      objectTypeLabel,
      scopeObjectSubType,
      scopeObjectType,
    ],
  );

  const isEmpty = rows.length === 0;

  return (
    <Box
      className={`${styles.dataGridPanel}${
        isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
      }`}
      sx={{
        ...aiPlanDataGridPanelSx,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 480,
      }}
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        rowHeight={48}
        checkboxSelection
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        isRowSelectable={isRowSelectable}
        disableRowGrouping
        disableAggregation
        disablePivoting
        disableColumnSorting
        disableColumnFilter
        disableDensitySelector={false}
        loading={loading}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        showToolbar
        showColumnVerticalBorder={false}
        disableColumnPinning={false}
        pinnedColumnsSectionSeparator="border"
        slots={{
          toolbar: toolbarSlot,
          noRowsOverlay: noRowsOverlaySlot,
          columnsManagement: columnsManagementSlot,
          baseCheckbox: ThinCheckbox,
        }}
        initialState={{
          pinnedColumns: {
            right: ["actions"],
          },
        }}
        sx={
          [
            getRegulationsListDataGridSx(primaryColor),
            aiHubListDataGridRowBorderSx,
            ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
            {
              height: "100%",
              minHeight: 400,
              // Hide any residual quick-filter / search UI from the grid chrome.
              "& .MuiDataGrid-toolbarQuickFilter": {
                display: "none",
              },
              "& .ai-hub-actions-cell": {
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",
                "& .MuiDataGrid-cellContent": {
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  width: "100%",
                  height: "100%",
                  overflow: "visible",
                },
              },
              "& .ai-hub-actions-cell.MuiDataGrid-cell--pinnedRight": {
                boxShadow: "none",
              },
            },
          ] as SxProps<Theme>
        }
      />
    </Box>
  );
}
